import { parse } from "date-fns"
import { useForm } from "react-hook-form"
import { validatePrecision } from "src/main/PointOfSale/CurrentSalePanel/validators"
import { getDaysInMonth } from "src/main/Settings/PublicProfile/Fuel/FuelDockAvailability"

export const useFuelDockForm = ({
  fuelDockAvailability,
  fuelOptions,
  fuelMeasurementUnit,
}) => {
  return useForm({
    defaultValues: getDefaultValues(
      fuelDockAvailability,
      fuelOptions,
      fuelMeasurementUnit
    ),
    resolver: fuelResolver,
  })
}

const safeParseTimeString = (dateString) => {
  if (
    !dateString ||
    typeof dateString !== "string" ||
    dateString.trim() === ""
  ) {
    return ""
  }
  try {
    const parsedDate = parse(dateString, "HH:mm", new Date())
    // Check if the parsed date is valid
    if (isNaN(parsedDate.getTime())) {
      return ""
    }
    return parsedDate
  } catch (error) {
    console.warn(`Error parsing date: ${dateString}`, error)
    return ""
  }
}

// Helper function to build default values
const getDefaultValues = (
  fuelDockAvailability,
  fuelOptions,
  fuelMeasurementUnit
) => {
  // Create a helper to process each day's time range
  const processTimeRange = (dayData) => ({
    enabled: dayData?.enabled || false,
    timeRange: {
      start: safeParseTimeString(dayData?.timeRange?.start),
      end: safeParseTimeString(dayData?.timeRange?.end),
    },
  })

  // Process all days at once
  const days = {
    monday: processTimeRange(fuelDockAvailability?.days?.monday),
    tuesday: processTimeRange(fuelDockAvailability?.days?.tuesday),
    wednesday: processTimeRange(fuelDockAvailability?.days?.wednesday),
    thursday: processTimeRange(fuelDockAvailability?.days?.thursday),
    friday: processTimeRange(fuelDockAvailability?.days?.friday),
    saturday: processTimeRange(fuelDockAvailability?.days?.saturday),
    sunday: processTimeRange(fuelDockAvailability?.days?.sunday),
  }

  return {
    fuelDockAvailability: {
      enabled: Boolean(fuelDockAvailability?.enabled),
      season: {
        yearRound: fuelDockAvailability?.season?.yearRound || false,
        startMonth: fuelDockAvailability?.season?.startMonth || "",
        startDay: fuelDockAvailability?.season?.startDay || "",
        endMonth: fuelDockAvailability?.season?.endMonth || "",
        endDay: fuelDockAvailability?.season?.endDay || "",
      },
      days,
    },
    fuelOptions: fuelOptions.reduce((acc, option) => {
      acc[option.label] = option.value || ""
      return acc
    }, {}),
    fuelMeasurementUnit,
  }
}

// Validation resolver function
const fuelResolver = (values) => {
  const errors = {}

  // Only validate if fuel dock availability is enabled
  if (values.fuelDockAvailability.enabled) {
    validateDaysEnabled(values, errors)
    validateSeasonDates(values, errors)
    validateTimeRanges(values, errors)
  }

  // Always validate fuel options regardless of fuel dock availability
  validateFuelOptions(values, errors)

  return {
    values,
    errors,
  }
}

const validateFuelOptions = (values, errors) => {
  const { fuelOptions } = values

  if (!fuelOptions) return

  // Validate each fuel option price
  Object.entries(fuelOptions).forEach(([label, price]) => {
    if (price === undefined || price === null || price === "") {
      return
    }

    // Validate minimum value
    const numericPrice = parseFloat(price)
    if (numericPrice < 0) {
      if (!errors.fuelOptions) errors.fuelOptions = {}
      errors.fuelOptions[label] = {
        type: "min",
        message: "Price cannot be less than $0.00",
      }
      return
    }

    // Validate precision (maximum 4 decimal places)
    const validatePrecisionResult = validatePrecision({
      name: "Price",
      precision: 4,
    })(price)
    if (validatePrecisionResult) {
      if (!errors.fuelOptions) errors.fuelOptions = {}
      errors.fuelOptions[label] = {
        type: "precision",
        message: validatePrecisionResult,
      }
    }
  })
}

// Helper validation functions
const validateDaysEnabled = (values, errors) => {
  const days = values.fuelDockAvailability.days
  const hasDayEnabled = Object.values(days).some((day) => day.enabled)

  if (!hasDayEnabled) {
    ensureErrorPath(errors, "fuelDockAvailability.root")
    errors.fuelDockAvailability.root = {
      message:
        "At least one day must be selected in order to display hours of operation",
      type: "validate",
    }
  }
}

const validateSeasonDates = (values, errors) => {
  const { season } = values.fuelDockAvailability

  // Check if all season dates are provided
  if (
    !season.startMonth ||
    !season.startDay ||
    !season.endMonth ||
    !season.endDay
  ) {
    validateRequiredSeasonFields(season, errors)
  }

  // Validate days are valid for selected months
  validateDayInMonth(season, "start", errors)
  validateDayInMonth(season, "end", errors)

  // Validate end date is after start date if within same month
  validateSameMonthDates(season, errors)
}

const validateRequiredSeasonFields = (season, errors) => {
  if (season.yearRound) {
    return
  }

  if (!season.startMonth) {
    ensureErrorPath(errors, "fuelDockAvailability.season.startMonth")
    errors.fuelDockAvailability.season.startMonth = {
      type: "required",
      message: "Start month is required",
    }
  }

  if (season.startMonth && !season.startDay) {
    ensureErrorPath(errors, "fuelDockAvailability.season.startDay")
    errors.fuelDockAvailability.season.startDay = {
      type: "required",
      message: "Start day is required",
    }
  }

  if (!season.endMonth) {
    ensureErrorPath(errors, "fuelDockAvailability.season.endMonth")
    errors.fuelDockAvailability.season.endMonth = {
      type: "required",
      message: "End month is required",
    }
  }

  if (season.endMonth && !season.endDay) {
    ensureErrorPath(errors, "fuelDockAvailability.season.endDay")
    errors.fuelDockAvailability.season.endDay = {
      type: "required",
      message: "End day is required",
    }
  }
}

const validateDayInMonth = (season, prefix, errors) => {
  const monthKey = `${prefix}Month`
  const dayKey = `${prefix}Day`

  if (season[monthKey] && season[dayKey]) {
    const daysInMonth = getDaysInMonth(season[monthKey])
    if (!daysInMonth.includes(parseInt(season[dayKey]))) {
      ensureErrorPath(errors, `fuelDockAvailability.season.${dayKey}`)
      errors.fuelDockAvailability.season[dayKey] = {
        type: "validate",
        message: "Invalid day for selected month",
      }
    }
  }
}

const validateSameMonthDates = (season, errors) => {
  if (
    season.startMonth &&
    season.startDay &&
    season.endMonth &&
    season.endDay &&
    season.startMonth === season.endMonth
  ) {
    if (parseInt(season.startDay) >= parseInt(season.endDay)) {
      ensureErrorPath(errors, "fuelDockAvailability.season.endDay")
      errors.fuelDockAvailability.season.endDay = {
        type: "validate",
        message: "End date must be after start date if within the same month",
      }
    }
  }
}

const validateTimeRanges = (values, errors) => {
  const days = values.fuelDockAvailability.days

  Object.entries(days).forEach(([dayName, day]) => {
    if (day.enabled) {
      const startTime = day.timeRange.start
      const endTime = day.timeRange.end

      // Only proceed if we have errors to report
      if (
        !startTime ||
        !endTime ||
        (startTime && endTime && startTime >= endTime)
      ) {
        // Create path once
        ensureTimeRangeErrorPath(errors, dayName)

        // Set specific errors
        if (!startTime) {
          errors.fuelDockAvailability.days[dayName].timeRange.start = {
            type: "required",
            message: "Start time is required",
          }
        }

        if (!endTime) {
          errors.fuelDockAvailability.days[dayName].timeRange.end = {
            type: "required",
            message: "End time is required",
          }
        }

        if (startTime && endTime && startTime >= endTime) {
          errors.fuelDockAvailability.days[dayName].timeRange.end = {
            type: "validate",
            message: "End time must be after start time",
          }
        }
      }
    }
  })
}

// Helper for creating the time range error path
const ensureTimeRangeErrorPath = (errors, dayName) => {
  if (!errors.fuelDockAvailability) errors.fuelDockAvailability = {}
  if (!errors.fuelDockAvailability.days) errors.fuelDockAvailability.days = {}
  if (!errors.fuelDockAvailability.days[dayName])
    errors.fuelDockAvailability.days[dayName] = {}
  if (!errors.fuelDockAvailability.days[dayName].timeRange)
    errors.fuelDockAvailability.days[dayName].timeRange = {}
}

// Helper function to ensure the error object path exists
const ensureErrorPath = (errors, path) => {
  const parts = path.split(".")
  let current = errors

  // Build the nested structure for all path parts except the last one
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]
    if (!current[part]) {
      current[part] = {}
    }
    current = current[part]
  }
}
